import { type VisualizationData } from '~components/business-owner/barry/chart/BarryChart';

import httpClient from '~api/httpClient';

interface BarryMessageRequest {
	message: string;
	conversationId?: string;
}

interface BarryConversationsPerUser {
	limit?: number;
	offset?: number;
}

interface BarryWebsocketUrlRequest {
	businessId: number;
	userId: number;
}

export interface BarryConversation {
	id: string;
	userId: number;
	businessId: number;
	title?: string;
	createdAt: Date;
	updatedAt: Date;
	metadata?: {
		source: string;
		createdAt: string;
	};
	status: 'active' | 'archived';
	messageCount: number;
}

export interface BarryConversationList {
	conversations: BarryConversation[];
	total: number;
	hasMore: boolean;
	success: boolean;
}

export interface BarryMessagesList {
	id: string;
	conversationId: string;
	role: 'user' | 'assistant' | 'system';
	content: string;
	timestamp: Date;
	metadata?: {
		executionSummary?: {
			workflow?: string;
			stepsCompleted: number;
			totalSteps: number;
			duration: number;
			agentsCalled: string[];
			status?: string;
			interrupt?: boolean;
		};
		agentsCalled?: string[];
		workflowUsed?: string;
		duration?: number;
		tokens?: {
			prompt: number;
			completion: number;
		};
		error?: string;
		type?: string;
		stepId?: string;
		interrupt?: boolean;
		visualization?: VisualizationData;
	};
}

export interface BarryIndividualConversation {
	conversation: BarryConversation;
	messages: BarryMessagesList[];
	success: boolean;
}

export const sendMessageToBarry = async (payload: BarryMessageRequest) => {
	const { message, conversationId } = payload;
	return httpClient.post<{ success: boolean }>('/common/barry/message', {
		message,
		conversationId,
	});
};

export const getConversationsForUser = async (payload: BarryConversationsPerUser) => {
	const { limit = 20, offset = 0 } = payload;
	return httpClient.get<BarryConversationList>('/common/barry/conversations/user', {
		params: {
			limit,
			offset,
		},
	});
};

export const getIndividualConversation = async (conversationUuid: string) => {
	return httpClient.get<BarryIndividualConversation>(
		`/common/barry/conversations/${conversationUuid}`,
	);
};

export const fetchBarryWebsocketConnectionUrl = (payload: BarryWebsocketUrlRequest) => {
	const { businessId, userId } = payload;

	return httpClient.get<{ url: string }>('/websocket-connection/barry', {
		params: {
			businessId,
			userId,
		},
	});
};

export const searchBarryConversationsForUserAndBusiness = async (query: string) => {
	return httpClient.get<BarryConversationList>(
		'/common/barry/conversations/user/search',
		{
			params: {
				searchTerm: query,
			},
		},
	);
};
